<template>
    <div>
        <Button
            variant="link"
            text="My Store"
            @click="toggleMap">
            <div class="d-flex justify-content-center align-items-center">
                <span class="text-tall-medium selected-colour mr-4">Store Details</span>
                <Icon name="far fa-chevron-down fa-xs selected-colour" size="1x"/>
            </div>
        </Button>
        <Modal id="store-details-modal" class="store-details">
            <div class="position-relative">
                <ButtonIcon
                    size="tall"
                    class="close-btn"
                    icon="far fa-times"
                    ariaLabel="Close"
                    variant="borderless"
                    @click="toggleModalAnimation('store-details-modal')"/>
                <Tabs>
                    <Map ref="map" :merchant="merchant"/>
                    <Tab id="store-details" title="Store Details">
                        <div class="px-16">
                            <div class="border-bottom pt-16 d-flex justify-content-between align-items-start w-100">
                                <div class="mb-16 flex-column">
                                    <p class="text-grande-medium mb-12">{{ merchant.address.city }}</p>
                                    <p class="mb-4 text-midnight-700 text-tall-regular store-details__address">{{ displayStreetCityPostalCode(merchant.address) }}</p>
                                    <p><a class="text-primary-500 text-tall-regular" :href="`tel:${merchant.address.phone}`">{{ displayPhoneNumber(merchant.address) }}</a></p>
                                </div>
                                <Button variant="link" text="Get Directions" class="pointer text-primary-500 text-tall-regular mb-12" @click.stop="getDirections()"/>
                            </div>
                            <div class="border-bottom py-16">
                                <p class="text-midnight-800 text-tall-medium mb-12">Store Hours</p>
                                <div v-for="(hours, index) in storeHours" :key="index" class="pb-8 d-flex justify-content-between store-details__hours">
                                    <p class="text-midnight-700 text-tall-regular">{{ DAYS_OF_WEEKS_NAMES[hours.day] }}</p>
                                    <p class="text-midnight-700 text-tall-regular">{{ formatDateItem(hours) }}</p>
                                </div>
                            </div>
                            <div v-if="storeDetails && storeDetails.textDetails.content !== ''" class="py-16">
                                <div class="d-flex flex-column">
                                    <p class="text-midnight-800 text-tall-medium mb-12">Note</p>
                                    <!-- eslint-disable-next-line vue/no-v-html -->
                                    <p class="v-html" v-html="storeDetails.textDetails.content"/>
                                </div>
                            </div>
                        </div>
                    </Tab>

                    <Tab v-if="additionalDetails && additionalDetails?.textDetails?.title" id="store-locations" title="Locations">
                        <div class="px-16 py-16 d-grid grid-2 fluid-left">
                            <div>
                                <p class="text-tall-medium text-midnight-800">{{ merchant.businessName }}</p>
                                <p class="text-tall-regular text-midnight-700">{{ businessAddress }}</p>
                            </div>
                            <Icon
                                name="far fa-map-marker-alt"
                                class="border-circle selected-colour selected-colour-bg-light"
                                size="1-5x"/>
                        </div>
                        <div v-for="(link, index) in additionalLinks" :key="index" class="px-16 mr-4">
                            <div
                                class="border-top py-16 d-grid grid-2 fluid-left pointer"
                                role="link"
                                @click="() => goToLink(link.url)">
                                <div>
                                    <p class="text-tall-medium text-midnight-800">{{ link.title }}</p>
                                    <p class="text-tall-regular text-midnight-700">{{ link.description }}</p>
                                </div>
                                <ButtonIcon
                                    icon="far fa-chevron-right"
                                    ariaLabel="Next"
                                    variant="borderless"
                                    size="short"
                                    :disabled="false"/>
                            </div>
                        </div>
                    </Tab>
                </Tabs>
            </div>
        </Modal>
    </div>
</template>

<script setup lang="ts">

import { Button, ButtonIcon, Tabs, Tab, Icon, Modal } from '@helcim/blizzard';
import { computed, ref } from 'vue';
import { toggleModalAnimation } from '../../models/utilities/ToggleModalAnimation';
import {useSettings, useStoreHours} from '../../compositions';
import { useActors } from '../../compositions';
import { useDate } from '../../compositions';
import { useAddress } from '../../compositions';
import { useTextBlocks } from '../../compositions/useTextBlocks';
import TextBlock from '../../models/TextBlock';
import Map from '../general/Map.vue';
import {TEXT_BLOCK_TEXT_ID, DAYS_OF_WEEKS_NAMES, SOCIAL_MEDIA_LINKS} from '../../scripts/constants';
import {StoreHours} from '../../types';
import Link from '../../models/Link';

const { storeHours } = useStoreHours();
const { goSettings } = useSettings();
const { merchant } = useActors();
const { textBlocks } = useTextBlocks();
const { time24HrTo12Hr } = useDate();
const { displayStreetCityPostalCode, displayPhoneNumber, getAddressLine1 } = useAddress();
const map = ref(null);

const storeDetails = computed(() => {
    return textBlocks.value.find((textBlock: TextBlock) => textBlock.textId === TEXT_BLOCK_TEXT_ID.ABOUT_US);
});

const additionalDetails = computed(() => {
    return textBlocks.value.find((textBlock: TextBlock) => textBlock.textId === TEXT_BLOCK_TEXT_ID.ADDITIONAL_DETAILS);
});

const additionalLinks = computed(() => {
    const links:Link[] = [];
    goSettings.value?.links.forEach((link: Link) => {
        if(!SOCIAL_MEDIA_LINKS[link.title]) {
            links.push(link);
        }
    });
    return links;
});

const businessAddress = computed(() => {
    return getAddressLine1(merchant.value.address);
});

const formatDateItem =((hours: StoreHours) => {
    return hours.isOpen ? time24HrTo12Hr(hours.openTime) + ' to ' + time24HrTo12Hr(hours.closeTime) : 'Closed';
});

const getDirections = (() => {
    const options = {
        enableHighAccuracy: true,
    };
    navigator.geolocation.getCurrentPosition(position => {
        map.value.getDirectionsTo(position.coords.latitude, position.coords.longitude, businessAddress.value);
    }, () => ({}), options);
});

const goToLink = (url: string) => {
    if (!url.includes('http')) {
        url = `http://${url}`;
    }
    window.open(url, '_blank');
};

const toggleMap = () => {
    window.dispatchEvent(new Event('resize'));
    toggleModalAnimation('store-details-modal');
};
</script>
